import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { DateTime } from 'luxon'
import { merge, cloneDeep } from 'lodash'
import { useWidget } from '../../../providers/widget'
import { BookingWidgetConfig, resolveDefaultGuests } from '../config'
import { useUpsertQuote, fetchExternalCheckoutUrl, type Quote } from '../data'
import { useFetchProperty, useFetchAvailability } from '../../calendar/data'
import { parseWidgetConfig } from '@homerunner-next/widget-core/schema'
import { registerPublisher, type QuotePublisher } from '../../../common/guest/quote-channel'
import { useShadowHost } from '../../../common/shadow-host-context'
import { isCentralPropertyUuid } from '../../../common/guest/central-uuid'
import { humanizeError } from '../../../common/error-map'
import { recordPagePropertyView } from '../../../common/guest/page-property'
import { calendarWidgetConfig, CalendarWidgetConfig } from '../../calendar/config'
import { getFinalSettings } from '../../../hr'
import type { PartyParams } from '../../../common/occupancy'
import {
  INFANTS_MAX,
  PETS_MAX,
  clampGuestTotal,
  clampParty,
  guardPets,
  readPartyParams,
  resolveOccupancyGuard,
} from '../../../common/occupancy'
import { useFetchWidget } from '../../../hooks'
import { useLocaleDetection } from '../../../hooks/locale'
import { useLocalizedTranslation } from '../../../common/i18n'
import { resolveLocaleTag } from '../../../common/locale-format'
import { Dialog, DialogContent } from '../../../components/ui/dialog'
import { useBookingCss } from '../hooks/useBookingCss'
import { useIsMobile } from '../hooks/useIsMobile'
import { resolveFeeLines, reshapeRatePlan } from '../fee-line-separator'
import { resolveRatePlanControl, applyRatePlanControl, type ControlledRatePlan } from '../rate-plan-control'
import { resolvePriceBreakdown, buildBreakdownRows, BUCKET_GROUP_LABELS } from '../../../common/price-breakdown/breakdown'
import { isRoundingStrikeWorthShowing, originalTotalOf } from '../../../common/rounding-strike-guard'
import { InlinePriceBreakdown } from '../../../common/price-breakdown/PriceBreakdownView'
import CalendarModal from './CalendarModal'
import PriceDetailsModal from './PriceDetailsModal'
import StickyBar, { StickyBarState } from './StickyBar'
import { trailingSlashModeOf, withTrailingSlash } from '../../../common/trailing-slash'

type UrlParams = PartyParams & { checkin?: string; checkout?: string }

// Party counts (adults/children/guests/pets) are parsed and sanitized by the shared
// occupancy guard, so the explorer link and this reader can't drift apart.
// Called from a mount effect only, never during render (#1003 item 2): a
// render-time read is what made the hydration pass diverge from the server HTML.
function getUrlParams(): UrlParams {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  return {
    checkin: params.get('checkin') || undefined,
    checkout: params.get('checkout') || undefined,
    ...readPartyParams(params),
  }
}

// Explicit initial guest count named by the URL, or undefined when the link
// carries none. Explorer links carry `adults`, `children`, AND their sum as
// `guests` (see the explorer's PropertyCard): with the breakdown on, `guests`
// here means ADULTS, so reading the sum would turn "2 adults + 2 children" into
// "4 adults". Prefer the explicit `adults` param in that mode; when only the sum
// arrives, back out the children to recover the adults count. Nullish checks
// throughout: an explicit `adults=0` is a stated value (the party is the
// children), not an absent param, and must not fall through to the sum. Without
// the breakdown `guests` stays the single total, exactly as before.
function resolveUrlInitialGuests(params: PartyParams, guestBreakdown: boolean): number | undefined {
  return guestBreakdown
    ? (params.adults
      ?? (params.guests !== undefined ? Math.max(0, params.guests - (params.children ?? 0)) : undefined))
    : (params.guests ?? params.adults)
}

function GuestStepper({ value, min, max, onChange, decLabel, incLabel, labelledBy, disabled, onAttemptOverMax }: {
  value: number
  min: number
  max: number
  onChange: (v: number) => void
  decLabel: string
  incLabel: string
  labelledBy?: string
  disabled?: boolean
  /**
   * Toolkit parity (guest-picker.js:266-283, `showPetsTooltip`): when set, the
   * Increase button stays clickable at `max` (a CSS "at max" style, not a
   * native `disabled`) so an attempted increment past the ceiling can be
   * observed and surfaced, instead of a disabled button silently doing
   * nothing. Omit to keep the plain native-disabled-at-max behavior.
   */
  onAttemptOverMax?: () => void
}) {
  const atMax = value >= max
  return (
    <span className="bk-stepper" role="group" aria-labelledby={labelledBy}>
      <button
        type="button"
        className="bk-stepper-btn"
        aria-label={decLabel}
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - 1))}
      >
        &minus;
      </button>
      <span className="bk-stepper-value" aria-live="polite">{value}</span>
      <button
        type="button"
        className={`bk-stepper-btn${atMax && onAttemptOverMax ? ' bk-stepper-btn-at-max' : ''}`}
        aria-label={incLabel}
        disabled={disabled || (atMax && !onAttemptOverMax)}
        onClick={() => {
          if (atMax) { onAttemptOverMax?.(); return }
          onChange(Math.min(max, value + 1))
        }}
      >
        +
      </button>
    </span>
  )
}

export default function BookingForm() {
  const { options, feedId, widgetId, resolvedTheme } = useWidget<BookingWidgetConfig>()

  // #1424: the booking widget's own guest-facing strings — the last
  // guest-facing widget to get the locale-table layer (common/i18n). Same
  // resolution order as every other widget: operator value for the visitor
  // locale → operator value for another locale of the same language → the
  // shipped table (configured locale only, gated per common/i18n/index.ts) →
  // this en_US default.
  const { detectedLocale } = useLocaleDetection(options.language.useAutoDetectedLocale, options.language.locale)
  const { _t } = useLocalizedTranslation('booking', options.translations, detectedLocale, options.language)
  // #1429: prices (Intl.NumberFormat) and dates (Luxon) below follow this
  // same resolved locale — `null` for English/unset/unusable tags keeps
  // their pre-#1429 output byte-identical (see common/locale-format.ts).
  const localeTag = useMemo(() => resolveLocaleTag(detectedLocale), [detectedLocale])

  // URL params set by the explorer link. NOT read during render (#1003 item 2):
  // the server has no URL, so a render-time read made the client's first
  // (hydration) render diverge from the server HTML. React threw the whole
  // server tree away and regenerated it, and on a cold load the visitor saw
  // "Add date" / "1 guest" for seconds despite a valid deep link. Same contract
  // as the ClientOnly gate the checkout and confirmation widgets use (#771): the
  // server AND the first client render both see no params, then the mount
  // effect below reads the real URL one tick later and seeds the fields.
  // `null` = not read yet, so nothing property-derived (defaultGuests, the
  // reconcile clamp, the quote) acts on a party the URL is about to overwrite.
  const [urlParams, setUrlParams] = useState<UrlParams | null>(null)
  const urlSeeded = urlParams !== null

  // Form state — config defaults on the server and on the first client render;
  // URL params take priority once they're seeded after mount.
  const [checkin, setCheckin] = useState<string | null>(options.initialCheckInDate || null)
  const [checkout, setCheckout] = useState<string | null>(options.initialCheckOutDate || null)
  const guestBreakdown = !!options.guestBreakdown
  // Explicit initial guest count (URL param or the fixed `initialGuests` option).
  // When set it wins over the property-relative `defaultGuests` (Min/Base/Max) setting.
  const urlInitialGuests = resolveUrlInitialGuests(urlParams ?? {}, guestBreakdown)
  // The schema rejects `initialGuests: 0` (#814), so a stored value is always >= 1.
  const explicitInitialGuests = urlInitialGuests ?? options.initialGuests
  // Default to 1 (never an empty selection) unless an explicit initial is given (#536).
  const [guests, setGuests] = useState(options.initialGuests || 1)
  // Pets/Infants rows are admin-toggleable within the breakdown picker: pets on
  // by default, infants off unless a PMS/integration consumes infant counts.
  const showPets = guestBreakdown && options.showPets !== false
  const showInfants = guestBreakdown && !!options.showInfants
  const [childrenCount, setChildrenCount] = useState(0)
  const [infantsCount, setInfantsCount] = useState(0)
  const [petsCount, setPetsCount] = useState(0)

  // Seed from the URL after hydration (see `urlParams` above). Children ride
  // along so an arriving filter keeps its composition, not just its total, and
  // pets so a visitor who searched "Pets: Yes" arrives with the pet already on
  // the form instead of having to remember it a second time. The property still
  // gets the last word: the reconcile effect below re-runs on the seeded values,
  // so a URL party or pets count above the property's cap clamps (and surfaces
  // the at-cap note) exactly as it did when the seed happened at render time.
  useEffect(() => {
    const params = getUrlParams()
    if (params.checkin) setCheckin(params.checkin)
    if (params.checkout) setCheckout(params.checkout)
    const seededGuests = resolveUrlInitialGuests(params, guestBreakdown)
    if (seededGuests !== undefined) setGuests(seededGuests || 1)
    if (guestBreakdown && params.children) setChildrenCount(params.children)
    if (showPets && params.pets) setPetsCount(Math.min(params.pets, PETS_MAX))
    setUrlParams(params)
    // Mount-only by design: the URL is read once, like ClientOnly's own mount flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [guestPickerOpen, setGuestPickerOpen] = useState(false)
  // Closing the picker unmounts whatever inside it holds focus (the Close button,
  // a stepper), which would drop focus to <body> — return it to the trigger. Only
  // for explicit dismissals (Close, Escape); the trigger's own toggle already IS
  // the focused element, and an outside click moved focus deliberately.
  const guestTriggerRef = useRef<HTMLButtonElement | null>(null)
  const guestPanelId = `bk-guest-panel-${widgetId}`
  const closeGuestPicker = () => {
    setGuestPickerOpen(false)
    guestTriggerRef.current?.focus()
  }
  // Once the visitor picks a guest count manually, stop auto-applying `defaultGuests`.
  const userPickedGuestsRef = useRef(false)
  const [couponInput, setCouponInput] = useState(options.couponCode || '')
  const [appliedCoupon, setAppliedCoupon] = useState(options.couponCode || '')
  const [showCouponInput, setShowCouponInput] = useState(!!options.couponCode)
  const [ratePlanSelected, setRatePlanSelected] = useState<string | null>(null)

  // UI state
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [priceDetailsOpen, setPriceDetailsOpen] = useState(false)
  const [widgetModalOpen, setWidgetModalOpen] = useState(false)
  const [checkoutLoading, setCheckoutLoading] = useState(false)
  const [quoteNotice, setQuoteNotice] = useState<{ text: string; type: string } | null>(null)
  const [couponNotice, setCouponNotice] = useState<{ text: string; type: string } | null>(null)
  // True while a committed coupon is being re-quoted — spins the Apply button and keeps
  // the overview mounted (instead of collapsing to the skeleton) during the fetch (#627).
  const [couponApplying, setCouponApplying] = useState(false)

  const calendarWidgetId = options.calendarWidgetId
  const skipCalendarClose = useRef(false)
  const widgetBodyRef = useRef<HTMLDivElement | null>(null)

  // Floating bar mode (#604, WP sticky-widget parity): below 960px the bar
  // replaces the in-page widget; the full form opens in a modal from the bar.
  const cssString = useBookingCss()
  const isMobile = useIsMobile()
  const stickyBarActive = !!options.sticky?.enabled && isMobile
  // WP `priceFromModalWidget` dance: the price-details overlay can't receive
  // clicks while a Radix modal is open (outside content is inert), so opening
  // it from inside the widget modal closes that modal and restores it after.
  const detailsFromWidgetModalRef = useRef(false)

  // Fetch the calendar widget's actual stored settings from the API
  const { data: calendarWidgetData } = useFetchWidget<CalendarWidgetConfig>(feedId, calendarWidgetId)

  // Filter for API calls.
  //
  // `platform` falls back to null, NOT ''. This object is the react-query key
  // for useFetchProperty and useFetchAvailability (both keyed on the whole
  // filter), and react-query hashes the object — so '' and null are different
  // cache entries. The SSR renderer seeds ['property', { platform: null, ... }]
  // and the calendar widget passes its own options.filter through with null, so
  // '' made this widget miss the seeded property record and refetch it, and
  // fetch availability a SECOND time under a key the calendar could not share.
  // It also appended a meaningless `platform=` to the property-calendar URL,
  // since fetchAvailability skips null/undefined but stringifies ''.
  const filter = useMemo(() => ({
    feed_id: options.filter?.feed_id ?? feedId,
    platform: options.filter?.platform ?? null,
    property: options.filter?.property ?? '',
  }), [options.filter, feedId])

  // Fetch property data for max guests, currency, min stay
  const { data: propertyData, isError: propertyError } = useFetchProperty(filter)
  // Prefetch calendar availability so the modal opens without a skeleton
  const { data: availability } = useFetchAvailability(filter)
  const property = propertyData?.result
  const currency = property?.currency_code || 'USD'
  const minStay = property?.minimum_stay || 1
  const bedroomCount = property?.bedroom_count || 0

  // Per-property occupancy, read through the guard the explorer also uses
  // (common/occupancy). It owns what `pets_allowed` and `maximum_guest_count` mean,
  // including the unresolved window: `useFetchProperty` keeps no placeholder, so
  // changing the filter blanks `property`, and a party picked for the PREVIOUS
  // property must not ride a quote for an unresolved one.
  const occupancy = useMemo(
    () => resolveOccupancyGuard(property, { failed: propertyError, feedMaxPets: options.maxPetsDefault }),
    [property, propertyError, options.maxPetsDefault],
  )
  const maxGuests = occupancy.maxGuests
  const petsLocked = occupancy.petsLocked
  // Toolkit parity (GuestPicker.php:88-100 / guest-picker.js `showPetsTooltip`):
  // the over-cap note only fires against a REAL configured ceiling — the
  // property's own max_pets OR the feed-level pets-cap fallback (#1407) —
  // never the platform's own generic PETS_MAX constant. resolveOccupancyGuard
  // owns that distinction (occupancy.maxPetsConfigured), matching the
  // toolkit's own resolve order: per-property PMS value, else the
  // account-wide setting, else no cap/no tooltip.
  const hasConfiguredPetsCap = occupancy.maxPetsConfigured
  // Toolkit-exact copy (guest-picker.js:242-250), routed through customizable
  // widget strings the same way petNoticeText is, defaulting to that copy
  // verbatim. Singular gets its own string because "1 pets" reads wrong.
  const petsCapNoticeText = useMemo(() => {
    const n = occupancy.maxPets
    if (n === 1) return options.maxPetsNoticeSingular || 'This property allows a maximum of 1 pet'
    return (options.maxPetsNoticeText || 'This property allows a maximum of {count} pets').replace('{count}', String(n))
  }, [occupancy.maxPets, options.maxPetsNoticeSingular, options.maxPetsNoticeText])
  // Transient "at cap" note (toolkit: fadeIn(150), 3s hold, fadeOut(150),
  // guest-picker.js:250-254). Re-triggering resets the hold instead of
  // stacking timers, same as the toolkit's own clearTimeout(tooltipTimer).
  const [petsCapNoticeVisible, setPetsCapNoticeVisible] = useState(false)
  const petsCapNoticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const showPetsCapNotice = useCallback(() => {
    setPetsCapNoticeVisible(true)
    if (petsCapNoticeTimer.current) clearTimeout(petsCapNoticeTimer.current)
    petsCapNoticeTimer.current = setTimeout(() => setPetsCapNoticeVisible(false), 3000)
  }, [])
  useEffect(() => () => {
    if (petsCapNoticeTimer.current) clearTimeout(petsCapNoticeTimer.current)
  }, [])
  // Derived, never trusted from state, so a denial can't lose a race with an
  // in-flight quote.
  const submittedPets = guardPets(petsCount, occupancy)
  // Same for the party: a `guests=12` seeded from the URL must not outlive the
  // moment the property answers "max 5". The steppers' `max` only blocks the NEXT
  // increment, so the clamp has to be derived, not left to the controls.
  const submittedParty = useMemo(
    () => (guestBreakdown
      ? clampParty({ adults: guests, children: childrenCount }, occupancy)
      : { adults: clampGuestTotal(guests, occupancy), children: 0 }),
    [guestBreakdown, guests, childrenCount, occupancy],
  )
  // Infants and pets never count toward the bookable guest total. Read off the
  // clamped party so the summary, the quote, and the checkout hand-off all agree
  // even on the render before the reconcile effect lands.
  const totalGuests = submittedParty.adults + submittedParty.children

  // Reconcile the panel with what actually gets sent, once the property has spoken:
  // clear a pets count it disallows, pull an over-cap pets count down to the
  // resolved max_pets ceiling — the property's own value, or the feed-level
  // fallback when the property carries none (#1230, #1407 — a URL-seeded
  // value is only bounded by the global PETS_MAX when the seed lands, before
  // the property has loaded — this branch is only reachable when a REAL
  // configured cap is lower than that global bound, so it never fires off
  // the plain PETS_MAX fallback), and pull an over-capacity party back inside
  // the guest maximum. The pets clamp surfaces the same at-cap note a manual
  // over-cap click would (#1230 follow-up: no silent clamping) since it's
  // just as real a denial. All three are gated on a LOADED property so a
  // slow fetch never rewrites the visitor's input on its way in, and on the
  // URL seed having landed (#1003): on the first commit this effect runs after
  // the seed effect's setters in the same batch, so a clamp computed from the
  // pre-seed defaults (a stale closure) would overwrite the just-seeded party.
  useEffect(() => {
    if (!property || !urlSeeded) return
    if (!occupancy.petsAllowed && petsCount > 0) setPetsCount(0)
    else if (petsCount > occupancy.maxPets) { setPetsCount(occupancy.maxPets); showPetsCapNotice() }
    if (submittedParty.adults !== guests) setGuests(submittedParty.adults)
    if (guestBreakdown && submittedParty.children !== childrenCount) setChildrenCount(submittedParty.children)
  }, [property, urlSeeded, occupancy, petsCount, submittedParty, guests, childrenCount, guestBreakdown, showPetsCapNotice])

  // WP renderMinimumNights parity: once a check-in is picked (no checkout
  // yet), the minimum-stay line reflects that date's per-day min stay.
  const effectiveMinStay = useMemo(() => {
    if (checkin && !checkout) {
      const perDate = availability?.[checkin as keyof typeof availability]?.min_stay
      if (perDate && perDate > 0) return perDate
    }
    return minStay
  }, [checkin, checkout, availability, minStay])

  // Apply the "Default number of Guests" setting (Min/Base/Max) against the loaded
  // property. Skipped when there's an explicit initial guest count or once the
  // visitor has picked a value manually. If the setting is unset the count stays at
  // the default of 1 (#536).
  useEffect(() => {
    // Until the URL seed lands, `explicitInitialGuests` can't know whether the
    // link names a party this setting has to yield to (#1003).
    if (!urlSeeded) return
    if (userPickedGuestsRef.current || explicitInitialGuests !== undefined) return
    if (!options.defaultGuests || !property) return
    const resolved = resolveDefaultGuests(options.defaultGuests, property)
    if (resolved > 0) setGuests(resolved)
  }, [urlSeeded, property, options.defaultGuests, explicitInitialGuests])

  // Collapsed guest-field summary, Airbnb-style: "2 guests, 1 infant, 1 pet".
  const guestSummary = useMemo(() => {
    const parts = [`${totalGuests} ${_t(totalGuests === 1 ? 'unit_guest_one' : 'unit_guest_other')}`]
    if (showInfants && infantsCount > 0) parts.push(`${infantsCount} ${_t(infantsCount === 1 ? 'unit_infant_one' : 'unit_infant_other')}`)
    if (showPets && petsCount > 0) parts.push(`${petsCount} ${_t(petsCount === 1 ? 'unit_pet_one' : 'unit_pet_other')}`)
    return parts.join(', ')
  }, [totalGuests, showInfants, showPets, infantsCount, petsCount, _t])

  // Discount text from property data. The price factors are multipliers
  // (0.8 = pay 80% = save 20%) — convert like the calendar header offer does;
  // rendering the raw factor reads as "Save 0.8%".
  const discountText = useMemo(() => {
    const weeklyFactor = property?.weekly_price_factor
    const monthlyFactor = property?.monthly_price_factor
    const weekly = weeklyFactor && weeklyFactor > 0 ? Math.round((1 - weeklyFactor) * 100) : 0
    const monthly = monthlyFactor && monthlyFactor > 0 ? Math.round((1 - monthlyFactor) * 100) : 0
    if (weekly > 0 && monthly > 0) return _t('discount_weekly_and_monthly').replace('{{weekly}}', String(weekly)).replace('{{monthly}}', String(monthly))
    if (weekly > 0) return _t('discount_weekly').replace('{{weekly}}', String(weekly))
    if (monthly > 0) return _t('discount_monthly').replace('{{monthly}}', String(monthly))
    return null
  }, [property, _t])

  // Listen for calendar date changes
  useEffect(() => {
    const handler = (e: Event) => {
      const { checkin: ci, checkout: co } = (e as CustomEvent).detail || {}
      // Linked calendar cleared its dates (Clear Dates button) → clear the
      // booking fields too, so the inputs and calendar stay in sync.
      if (!ci && !co) {
        setCheckin(null)
        setCheckout(null)
        setQuoteNotice(null)
        return
      }
      if (ci) {
        setCheckin(ci)
        setQuoteNotice(null)
      }
      if (co) {
        setCheckout(co)
        // Skip closing the modal when it's just the initial date sync echo
        if (skipCalendarClose.current) {
          skipCalendarClose.current = false
        } else {
          setCalendarOpen(false)
        }
      }
    }
    window.addEventListener(`calendar-dates-changed-${calendarWidgetId}`, handler)
    return () => window.removeEventListener(`calendar-dates-changed-${calendarWidgetId}`, handler)
  }, [calendarWidgetId])

  // Keep every linked calendar (the inline one on the page + the popup's own
  // calendar) in sync with the form's dates. CalendarModal only broadcasts while
  // it's visible, so when a completed selection closes the popup the final
  // check-out never reaches an inline calendar — broadcasting from the form fixes
  // that. Skip the initial mount AND the post-hydration URL seed (#1003), so
  // page-load behaviour is unchanged.
  const didSyncCalendarsRef = useRef(false)
  useEffect(() => {
    if (!calendarWidgetId || !urlSeeded) return
    if (!didSyncCalendarsRef.current) {
      didSyncCalendarsRef.current = true
      return
    }
    window.dispatchEvent(new CustomEvent(`calendar-set-dates-${calendarWidgetId}`, {
      detail: { checkin, checkout },
    }))
  }, [checkin, checkout, calendarWidgetId, urlSeeded])

  // Calculate nights
  const nights = useMemo(() => {
    if (!checkin || !checkout) return 0
    const ci = DateTime.fromISO(checkin)
    const co = DateTime.fromISO(checkout)
    return Math.round(co.diff(ci, 'days').days)
  }, [checkin, checkout])

  // Create quote when dates + guests ready. Gated on a settled occupancy guard:
  // until the property answers (or its fetch fails), `clampParty` can only clamp
  // against the display fallback, so an over-capacity party seeded from a URL or
  // carried across a property switch would reach Central. A failed fetch settles
  // permissive, so an unreachable property endpoint never blocks quoting. Also
  // waits for the URL seed (#1003) so a deep link never fires one quote for the
  // config defaults and a second for the party the visitor actually asked for.
  const quoteEnabled = !!checkin && !!checkout && totalGuests > 0 && !!filter.property && occupancy.settled && urlSeeded
  const { data: rawQuote, isLoading: quoteLoading, isFetching: quoteFetching, isPlaceholderData, error: quoteError } = useUpsertQuote({
    feed_id: filter.feed_id,
    platform: filter.platform || undefined,
    property: filter.property,
    checkin: checkin ? new Date(checkin + 'T00:00:00') : new Date(),
    checkout: checkout ? new Date(checkout + 'T00:00:00') : new Date(),
    guests: totalGuests,
    adults: guestBreakdown ? submittedParty.adults : undefined,
    children: guestBreakdown ? submittedParty.children : undefined,
    infants: showInfants ? infantsCount : undefined,
    pets: showPets ? submittedPets : undefined,
    coupon: appliedCoupon || undefined,
  }, quoteEnabled)

  // `keepPreviousData` holds the prior quote as a placeholder across a key change so the
  // overview doesn't unmount mid coupon-apply (#627). But a DISABLED query (no dates)
  // still reports that placeholder — so ignore both unless the query is actually enabled,
  // otherwise clearing the dates would leak a stale quote + a spurious spinner.
  const quote = quoteEnabled ? rawQuote : undefined
  const showingPlaceholder = quoteEnabled && isPlaceholderData

  // The quote on screen doesn't match the current inputs yet: the first load (no data)
  // or a stale placeholder held while a fresh quote is in flight. Drives the skeleton +
  // disabled states below.
  const quoteSettling = quoteLoading || showingPlaceholder

  // Handle quote error. A quote that fails WHILE a coupon is applied is a
  // coupon rejection (PMS like track hard-fail the whole quote on a bad
  // code, e.g. "Sorry, this coupon has either expired or is invalid.") —
  // don't let it unmount the booking overview and swallow the coupon input
  // (#600): surface the message under the input, drop the coupon, and let
  // the query refetch without it so pricing comes back.
  useEffect(() => {
    if (quoteError) {
      if (appliedCoupon) {
        setCouponNotice({ text: humanizeError(quoteError.message, 'coupon', _t('notice_coupon_failed')), type: 'danger' })
        setAppliedCoupon('')
        setShowCouponInput(true)
      } else {
        setQuoteNotice({ text: humanizeError(quoteError.message, 'quote', _t('notice_generic_error')), type: 'danger' })
      }
    } else if (quote) {
      setQuoteNotice(null)
    }
    // `_t` intentionally excluded (widget-core's useTranslation returns a new
    // closure every render, never memoized — see common/i18n/index.ts): with
    // it in the deps this effect re-fires every render and re-sets a fresh
    // notice OBJECT each time, which never bails out via Object.is and loops
    // forever. The current `_t` is still read from the render closure; a
    // locale change alone isn't expected to relabel an already-set notice,
    // matching how no other widget depends on `_t` inside a useEffect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteError, quote, appliedCoupon])

  // Did the quote actually honor the applied coupon? The authoritative signal is a
  // non-zero DISCOUNT-type invoice line on any rate plan: Central's QuoteLineItem maps
  // `coupon_discount` -> 'DISCOUNT', and a length-of-stay discount is a distinct
  // 'STAY_DISCOUNT' type, so this can't false-positive on a weekly/monthly discount.
  // We deliberately do NOT trust the quote's `coupons` echo — several PMS (track,
  // hostaway, guesty, streamlinevrs) echo the submitted code back regardless of whether
  // it was accepted (and track returns a bare string). PMS that reject invalid codes
  // hard (ownerrez/guesty/hostaway/lodgify/hostfully) surface that via `quoteError`;
  // the silent PMS all emit a DISCOUNT line when the coupon is honored.
  const couponHonored = useMemo(() => {
    if (!appliedCoupon || !quote) return false
    return !!quote.rateplans?.some(rp =>
      rp.invoiceItems?.some(item => item.type === 'DISCOUNT' && item.amount !== 0)
    )
  }, [appliedCoupon, quote])

  // Rate Plan Control (toolkit port, #1690): null unless the operator set at
  // least one row with match text (feature off, quote untouched). Computed from
  // the RAW quote so the honored-coupon check can't see a hidden plan vanish.
  const ratePlanRules = useMemo(() => resolveRatePlanControl({ ratePlanControl: options.ratePlanControl }), [options.ratePlanControl])
  const controlled = useMemo(() => {
    if (!quote?.rateplans?.length) return null
    if (!ratePlanRules) return { plans: quote.rateplans as ControlledRatePlan<Quote['rateplans'][number]>[], note: '' }
    return applyRatePlanControl(quote.rateplans, ratePlanRules, couponHonored)
  }, [quote, ratePlanRules, couponHonored])
  const visibleRatePlans = controlled?.plans ?? []
  const hiddenPlanNote = controlled?.note ?? ''

  // Auto-select first rate plan when quote loads
  useEffect(() => {
    if (visibleRatePlans.length) {
      if (!ratePlanSelected || !visibleRatePlans.find(rp => rp.id === ratePlanSelected)) {
        setRatePlanSelected(visibleRatePlans[0].id)
      }
    }
  }, [visibleRatePlans, ratePlanSelected])

  // Fee Line Separator (toolkit port, #1064 Wave 2): resolved once per
  // settings change; null when the operator hasn't configured any fee names
  // (feature off, quote passes through unchanged).
  const feeLinesConfig = useMemo(() => resolveFeeLines({ feeLines: options.feeLines }), [options.feeLines])

  // Current rate plan. Reshaped through the Fee Line Separator BEFORE
  // anything downstream reads it (nightlyRate's `totalRent` basis, the
  // published quote-channel total, the price-details modal), so every
  // consumer agrees on the same invoiceItems/totalRent.
  const currentRatePlan = useMemo(() => {
    if (!visibleRatePlans.length) return null
    const ratePlan = visibleRatePlans.find(rp => rp.id === ratePlanSelected) || visibleRatePlans[0]
    return feeLinesConfig ? reshapeRatePlan(ratePlan, feeLinesConfig) : ratePlan
  }, [visibleRatePlans, ratePlanSelected, feeLinesConfig])

  // Inline Price Breakdown (toolkit port, #1064 Wave 3): null unless the
  // operator explicitly enabled it (opt-in, like feeLines). Rows build off
  // the ALREADY-reshaped currentRatePlan, so the inline breakdown, the
  // modal, and the Wave 2 fee-line presentation always agree.
  const priceBreakdownConfig = useMemo(
    () => resolvePriceBreakdown({ priceBreakdown: options.priceBreakdown }),
    [options.priceBreakdown],
  )

  // Disclosure open-state lives HERE, not in the breakdown component: the
  // overview block unmounts while a re-quote settles (date changes), and WP
  // persists all three states on the widget across exactly those re-renders
  // (a coupon entry must not slam the accordion shut). `undefined` accordion
  // state means "never toggled" — only then does openByDefault apply.
  const [breakdownOpen, setBreakdownOpen] = useState<boolean | undefined>(undefined)
  const [breakdownExpOpen, setBreakdownExpOpen] = useState<Record<string, boolean>>({})
  const [breakdownTaxOpen, setBreakdownTaxOpen] = useState(false)

  // #1530 follow-up: the translated bucket labels for the grouped breakdown.
  // Keyed on the translation store + locale, not `_t` (widget-core's
  // useTranslation returns a fresh closure every render, see the notice
  // effect above), so `breakdownRows` keeps a stable identity across renders.
  const groupLabels = useMemo(() => ({
    rent: _t('group_rent', BUCKET_GROUP_LABELS.rent),
    fee: _t('group_fees', BUCKET_GROUP_LABELS.fee),
    manual: _t('group_addons', BUCKET_GROUP_LABELS.manual),
    refundable: _t('group_others', BUCKET_GROUP_LABELS.refundable),
    tax: _t('group_taxes', BUCKET_GROUP_LABELS.tax),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [options.translations, detectedLocale, options.language.useAutoDetectedLocale])

  const breakdownRows = useMemo(() => {
    if (!priceBreakdownConfig || !currentRatePlan) return null
    const rows = buildBreakdownRows(currentRatePlan.invoiceItems, {
      grouping: priceBreakdownConfig.grouping,
      rentStyle: priceBreakdownConfig.rentStyle,
      showTaxes: priceBreakdownConfig.showTaxes,
      showDeposit: priceBreakdownConfig.showDeposit,
      taxDisplay: priceBreakdownConfig.taxDisplay,
      taxLabel: priceBreakdownConfig.taxLabel,
      explanations: priceBreakdownConfig.explanations,
      nights,
      // Central passes PMS charge types through verbatim, so anything outside
      // the fixed bucket lists (RESORT_FEE, PET_FEE, OCCUPANCY_TAX, …) has no
      // bucket. Dropping those rows leaves an itemization that does not add up
      // to the Total sitting right beside it — the one thing a breakdown must
      // never do. Checkout and confirmation already include them.
      includeUnknown: true,
      // #1530 follow-up: grouped-mode group rows print the widget's translated
      // bucket labels; unset locales fall back to the shared English labels.
      groupLabels,
    })
    // Zero rows = nothing to say; never insert an empty shell.
    return rows.length > 0 ? rows : null
  }, [priceBreakdownConfig, currentRatePlan, nights, groupLabels])

  // The modal's exact-match relabel, shared with the inline rows (WP pipes
  // its labels through the same replacement filter). A rule applies only
  // when its `to` is a non-blank string: the save-path strip deliberately
  // keeps in-progress one-sided rows ({from: 'X', to: ''}), and `?? title`
  // alone would let that blank ERASE the charge title.
  const relabelPriceLabel = useCallback(
    (title: string) => {
      const to = (options.priceLabelReplacements ?? []).find(r => r.from === title)?.to
      return typeof to === 'string' && to.trim() !== '' ? to : title
    },
    [options.priceLabelReplacements],
  )

  // ── Guest quote channel (#1138) ──────────────────────────────────────────
  // Publish this widget's quote lifecycle onto the window registry so
  // separately-bundled consumer widgets (rate-comparison, split-cost, …) on
  // the same page can render against it. Contract:
  // common/guest/quote-channel.ts. Registration is mount-scoped (never
  // per-render), one instance per mount (#1148), keyed on the feed the quote
  // is fetched from — `filter.feed_id`, the same feed consumers subscribe on
  // — and carrying the mount host so a host a page script removes without
  // React cleanup stops counting as a live publisher (#1149). Shadow-less
  // mounts have no host and opt out of liveness (null).
  const shadowHost = useShadowHost()
  const quotePublisherRef = useRef<QuotePublisher | null>(null)
  useEffect(() => {
    const publisher = registerPublisher(filter.feed_id, widgetId, shadowHost)
    quotePublisherRef.current = publisher
    return () => {
      quotePublisherRef.current = null
      publisher.unregister()
    }
  }, [filter.feed_id, widgetId, shadowHost])

  // Recently-viewed producer (#1138): a resolved property fetch IS a
  // confirmed view — covers booking-only embeds where no property-details
  // widget is on the page. Once per distinct property per mount;
  // `recordPropertyView` validates, `pushRecentlyViewed` dedupes the list.
  // Page identity (#1151): a booking widget pinned to a property (manual
  // `data-hr-options` filter.property, or the dashboard Property select) on a
  // non-property page must not record on every load — the pushed
  // `filter.property` has to be the page's own slug/canonical, or the widget
  // has to sit inside a generated property page (`common/guest/page-property`).
  // `shadowHost` is the one declared above for the quote publisher — #1308 and
  // #1149 landed the same hook independently, and git merges both additions
  // cleanly into a duplicate declaration that does not compile.
  const lastRecordedViewRef = useRef<string | null>(null)
  useEffect(() => {
    const uuid = property?.uuid
    if (!uuid || lastRecordedViewRef.current === uuid) return
    lastRecordedViewRef.current = uuid
    recordPagePropertyView(uuid, { slugs: [filter.property], host: shadowHost })
  }, [property, filter.property, shadowHost])

  // Whether anything was published while the gate was up: a lost gate (dates
  // cleared) maps to 'cleared', while the pristine no-dates-yet state stays
  // 'pending' (the register seed) — consumers render those differently.
  // Same deps as the registration effect (plus the quote inputs), so a
  // re-registration is always followed by a publish onto the new instance.
  const publishedQuoteRef = useRef(false)
  useEffect(() => {
    const publisher = quotePublisherRef.current
    if (!publisher) return
    if (!quoteEnabled) {
      if (publishedQuoteRef.current) {
        publishedQuoteRef.current = false
        publisher.clear()
      }
      return
    }
    // The snapshot names the CANONICAL property identifier: the quote's own
    // property_id is a numeric internal id, so the publisher resolves it via
    // the property fetch already in scope (`Property.uuid` — Central's
    // identifier field, PROP-shaped today; see common/guest/central-uuid.ts)
    // and VALIDATES it, so a legacy/foreign token never leaks into the
    // channel. Unresolved/invalid publishes null — consumers must handle it.
    const centralPropertyUuid =
      property?.uuid && isCentralPropertyUuid(property.uuid) ? property.uuid : null
    publishedQuoteRef.current = true
    if (quoteError) {
      publisher.publish({
        status: 'error',
        centralPropertyUuid,
        total: null,
        currency: null,
        nights,
        checkin,
        checkout,
        ratePlanId: null,
      })
      return
    }
    if (!quote || !currentRatePlan || quoteSettling) {
      // Quote not settled for the CURRENT inputs: the first fetch (nothing to
      // hold) or a keepPreviousData-held placeholder while a refetch is in
      // flight (#627) — after a date change or a coupon apply, `quote` still
      // carries the OLD numbers, which must never be published as `ready`
      // against the new dates (the widget itself hides its numbers behind
      // quoteSettling too). Publish pending with the current dates/nights and
      // validated id, numbers null.
      publisher.publish({
        status: 'pending',
        centralPropertyUuid,
        total: null,
        currency: null,
        nights,
        checkin,
        checkout,
        ratePlanId: null,
      })
      return
    }
    // Settled quote for the current inputs: publishes on every settled quote
    // change AND on rate-plan selection. Currency comes from the property
    // fetch; if that failed, the widget's 'USD' is only a display fallback —
    // never publish it as a fact.
    publisher.publish({
      status: 'ready',
      centralPropertyUuid,
      total: currentRatePlan.total,
      currency: property ? currency : null,
      nights,
      checkin,
      checkout,
      ratePlanId: currentRatePlan.id,
    })
  }, [filter.feed_id, widgetId, shadowHost, quoteEnabled, quote, quoteError, quoteSettling, currentRatePlan, property, currency, nights, checkin, checkout])

  // Price formatting — locale changes the number format only; the currency
  // code and fraction-digit rules stay exactly as before (#1429).
  const formatPrice = useCallback((amount: number) => {
    const format = (locale: string) => new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount)
    if (!localeTag) return format('en-US')
    try {
      return format(localeTag)
    } catch {
      return format('en-US')
    }
  }, [currency, localeTag])

  // The total the widget shows: after tax, or pre-tax when totalAfterTax is off.
  // The Total row, the floating bar's total and the per-night figure all read
  // this one number so they reconcile with each other.
  const displayedTotal = useMemo(() => {
    if (!currentRatePlan) return null
    return options.totalAfterTax ? currentRatePlan.total : currentRatePlan.total - currentRatePlan.totalTaxes
  }, [currentRatePlan, options.totalAfterTax])

  // Pre-discount total struck beside the Total row (#1691, toolkit Original
  // Total module, opt-in via showOriginalTotal). Same figure the explorer card
  // prints for the same quote: displayedTotal + |discount|, gated by the
  // existing rounding guard (common/rounding-strike-guard.ts).
  const originalTotal = useMemo(() => {
    if (options.showOriginalTotal !== true || !currentRatePlan || !nights || displayedTotal === null) return null
    return originalTotalOf(displayedTotal, currentRatePlan.discount || 0, nights)
  }, [options.showOriginalTotal, currentRatePlan, nights, displayedTotal])

  // Per-night figure for the card AND the floating bar (#1064, toolkit
  // Per-Night Price Consistency). One memo feeds both so they cannot disagree.
  //   'total' (default): displayedTotal ÷ nights — nightly × nights equals the
  //     total beside it (toolkit module: WP price_with_dates = average_price).
  //   'rent': (totalRent − discount) ÷ nights — the bare WP plugin default
  //     (average_rent); fees and taxes sit outside the nightly number.
  // `original` is the pre-discount figure on the same basis, struck through
  // next to the active price when a discount applies (toolkit show_strikethrough,
  // options.showNightlyStrikethrough) AND the discount is large enough per
  // night to be a real discount rather than Central's sub-dollar rounding
  // reconciliation on a cents-priced rate (#1064, toolkit rounding-strike-guard,
  // common/rounding-strike-guard.ts — $1.00/night threshold).
  // `rateplan.discount` carries a PMS-dependent SIGN: Guesty sends it positive,
  // Hostaway / OwnerRez / Hostfully send it negative. `total` is post-discount
  // and `totalRent` pre-discount either way, so the magnitude is what matters —
  // same Math.abs convention as common/property-card/grid-quote-price.tsx.
  const nightlyRate = useMemo(() => {
    if (!currentRatePlan || !nights || displayedTotal === null) return null
    const discount = Math.abs(currentRatePlan.discount || 0)
    const strike = options.showNightlyStrikethrough !== false && discount > 0 && isRoundingStrikeWorthShowing(discount, nights)
    if (options.nightlyPriceBasis === 'rent') {
      return {
        price: (currentRatePlan.totalRent - discount) / nights,
        original: strike ? currentRatePlan.totalRent / nights : null,
      }
    }
    return {
      price: displayedTotal / nights,
      original: strike ? (displayedTotal + discount) / nights : null,
    }
  }, [currentRatePlan, nights, displayedTotal, options.nightlyPriceBasis, options.showNightlyStrikethrough])

  // Per-bedroom price hint (WP `bedroom_price` parity): the nightly price
  // divided by the bedroom count, shown only for multi-bedroom properties.
  const renderBedroomHint = useCallback((nightly: number) => {
    if (!options.showBedroomPrice || bedroomCount <= 1) return null
    const perBedroom = Math.round(nightly / bedroomCount)
    const text = (options.perBedroomText || 'Only {price} per bedroom!').replace('{price}', formatPrice(perBedroom))
    return (
      <>
        <span className="rent-bedroom-sep"> &middot; </span>
        <span className="bedroom-price-text">{text}</span>
      </>
    )
  }, [options.showBedroomPrice, options.perBedroomText, bedroomCount, formatPrice])

  // Format date for display — the operator's configured `dateFormat`
  // pattern is unchanged; only the locale (month/weekday names) follows
  // the widget's resolved locale (#1429).
  const formatDate = useCallback((dateStr: string) => {
    const pattern = options.dateFormat || 'MMM d, yyyy'
    const dt = DateTime.fromISO(dateStr)
    if (!localeTag) return dt.toFormat(pattern)
    try {
      return dt.setLocale(localeTag).toFormat(pattern)
    } catch {
      return dt.toFormat(pattern)
    }
  }, [options.dateFormat, localeTag])

  // Calendar config for modal - fetched widget options merged with booking overrides
  const calendarConfig = useMemo(() => {
    if (!calendarWidgetData) return null

    // Merge feed branding + calendar widget stored settings
    const baseConfig = getFinalSettings<CalendarWidgetConfig>(calendarWidgetData.widget, calendarWidgetData.feed)

    // Apply booking-specific overrides on top
    const merged = merge({}, cloneDeep(baseConfig), {
      filter: {
        feed_id: filter.feed_id,
        platform: filter.platform,
        property: filter.property,
      },
      language: options.language,
      // Defense in depth (#1002 item 3): the grid-level fix (848bf8d8,
      // CalendarGrid.tsx) already resolves the embedded calendar's OWN
      // property fetch first, so this thread doesn't change what's on
      // screen today — but it covers any future consumer of the embedded
      // calendar's options that reads `currency` directly.
      currency,
      allowSelectDates: true,
      showFooterInfo: false,
      showHeaderOffer: false,
      showLocalTime: false,
      showClearButton: true,
      showPrice: true,
      // The booking widget renders the calendar inside its own modal —
      // never let the calendar widget's modal display mode leak in here.
      displayMode: 'inline' as const,
      // Force the calendar's section title off — the booking modal has its
      // own header, so the embedded calendar shouldn't render a duplicate
      // "Rates and Availability" heading.
      section: { show: false },
    })

    // parseWidgetConfig, not a bare .parse: `baseConfig` is the calendar
    // widget's RAW stored settings (getFinalSettings runs no schema), so a
    // nullified '' draft anywhere in the customer's calendar config would
    // brick the whole booking widget on the checkout path (#1171 review).
    // Strip-only pre: the default liftLegacySpacing would promote a legacy
    // `width` pair into a maxWidth CONSTRAINT inside the booking modal, where
    // the bare parse always stripped it as an unknown key — spacing is the
    // host modal's concern here, like displayMode/section above.
    return parseWidgetConfig(calendarWidgetConfig, merged, (v) => v)
  }, [filter, options.language, currency, calendarWidgetData])

  // Apply coupon handler. Committing a new code feeds it into the quote query (a
  // re-fetch); `couponApplying` spins the button and keeps the overview mounted.
  const handleApplyCoupon = useCallback(() => {
    if (!quote?.id) {
      setCouponNotice({ text: _t('notice_coupon_needs_dates'), type: 'warning' })
      return
    }
    const code = couponInput.trim()
    if (!code) {
      setCouponNotice({ text: _t('notice_coupon_needs_code'), type: 'warning' })
      return
    }
    // Re-applying the already-applied code doesn't change the query key (no re-fetch
    // fires), so just re-surface the current verdict instead of spinning forever.
    if (code === appliedCoupon) {
      setCouponNotice(couponHonored
        ? { text: _t('notice_coupon_applied'), type: 'success' }
        : { text: _t('notice_coupon_invalid'), type: 'warning' })
      return
    }
    setCouponApplying(true)
    setCouponNotice(null)
    setAppliedCoupon(code)
  }, [couponInput, quote, appliedCoupon, couponHonored, _t])

  // Clear the applying flag once the re-quote settles (success or error).
  useEffect(() => {
    if (!quoteFetching) setCouponApplying(false)
  }, [quoteFetching])

  // Reflect whether the quote honored the coupon — never claim success on a 200 that
  // silently dropped an invalid/expired code. Judge only FRESH data: `quoteFetching`
  // stays true (with the previous quote kept as placeholder) until the re-quote lands.
  useEffect(() => {
    if (!appliedCoupon || quoteFetching) return
    if (quoteError || !quote) return
    setCouponNotice(couponHonored
      ? { text: _t('notice_coupon_applied'), type: 'success' }
      : { text: _t('notice_coupon_invalid'), type: 'warning' })
    // `_t` intentionally excluded — see the identical rationale on the
    // quoteError effect above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedCoupon, quote, quoteFetching, quoteError, couponHonored])

  // Checkout handler
  const handleCheckout = useCallback(async () => {
    if (!checkin || !checkout) {
      if (options.showDatepicker) {
        setCalendarOpen(true)
      } else {
        setQuoteNotice({ text: _t('notice_select_dates'), type: 'warning' })
      }
      return
    }
    if (!totalGuests) {
      setQuoteNotice({ text: _t('notice_select_guests'), type: 'warning' })
      return
    }
    if (!quote?.id) return

    // The button stays loading + disabled through a redirect (#601) — the page
    // remains interactive while the browser navigates, and resetting here
    // reopened a double-submit window. Loading is only cleared on error, on
    // the same-page checkout path, or by the pageshow handler when the
    // visitor navigates back (bfcache restore).
    setCheckoutLoading(true)
    try {
      // #443: external-PMS properties (reservation_target === 'hosted') reserve + pay
      // entirely on the PMS. Send the guest straight to the PMS hosted checkout instead
      // of the local checkout; surface an error rather than dead-ending if it can't resolve.
      if (property?.reservation_target === 'hosted') {
        try {
          window.location.href = await fetchExternalCheckoutUrl(quote.id, filter.feed_id)
        } catch {
          setQuoteNotice({ text: _t('notice_hosted_checkout_failed'), type: 'danger' })
          setCheckoutLoading(false)
        }
        return
      }

      // Get checkout route from feed config (set in feed's reverseProxy routes)
      // #1848: on the feed's preferred trailing-slash form, so the jump to
      // checkout is one hop and not a redirect per booking.
      const checkoutRoute = withTrailingSlash(
        calendarWidgetData?.feed?.additional_info?.reverseProxy?.routes?.checkout || '/checkout',
        trailingSlashModeOf(calendarWidgetData?.feed),
      )

      // If a checkout widget exists on the same page, dispatch event to it
      // (single-page checkout scenario)
      const checkoutWidgetOnPage = document.querySelector('[data-hr-widget="checkout"]')
      if (checkoutWidgetOnPage) {
        window.dispatchEvent(new CustomEvent('checkout-quote-ready', {
          detail: { quoteId: quote.id, ratePlanId: ratePlanSelected },
        }))
        setCheckoutLoading(false)
        return
      }

      // Redirect to checkout page with quote params
      const sep = checkoutRoute.includes('?') ? '&' : '?'
      let url = `${checkoutRoute}${sep}qid=${encodeURIComponent(quote.id)}`
      if (ratePlanSelected) url += `&rpid=${encodeURIComponent(ratePlanSelected)}`
      window.location.href = url
    } catch {
      setQuoteNotice({ text: _t('notice_generic_error'), type: 'danger' })
      setCheckoutLoading(false)
    }
  }, [checkin, checkout, totalGuests, quote, filter.feed_id, ratePlanSelected, calendarWidgetData, options.showDatepicker, property?.reservation_target, _t])

  // Coming BACK from checkout restores this page from the bfcache with
  // checkoutLoading still true — re-enable the button (#601).
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setCheckoutLoading(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  // Button text based on reservation method (matching WP plugin), each label configurable.
  const bookingButtonText = useMemo(() => {
    const method = property?.reservation_method
    const t = options.buttonText
    if (method === 'instant') return t?.instant || 'Instant Booking'
    if (method === 'request') return t?.request || 'Send Request'
    if (method === 'inquiry') return t?.inquiry || 'Get a Quote'
    return t?.fallback || 'Book Now'
  }, [property?.reservation_method, options.buttonText])

  const buttonText = useMemo(() => {
    if (checkoutLoading) return _t('button_processing')
    if (!checkin || !checkout) return _t('button_check_availability')
    // During a coupon apply keep the booking label (only the Apply button spins); for
    // date/guest changes still show Processing while the fresh quote settles.
    if (quoteSettling && !couponApplying) return _t('button_processing')
    return bookingButtonText
  }, [checkin, checkout, quoteSettling, couponApplying, checkoutLoading, bookingButtonText, _t])

  const isButtonDisabled = useMemo(() => {
    if (!checkin || !checkout) return false // clicking opens calendar
    if (!totalGuests) return true
    if (quoteSettling || checkoutLoading) return true
    if (quoteError && !quote) return true
    return false
  }, [checkin, checkout, totalGuests, quoteSettling, checkoutLoading, quoteError, quote])

  // Price-details open/close, routing through the WP-style widget-modal dance.
  // #919: when the floating BAR opened the details, the bar unmounts for the
  // modal's lifetime, so the modal cannot hand focus back to the price line
  // that opened it (gone by the time the modal mounts). Remember it here and
  // refocus the remounted bar's price line once the modal is closed.
  const detailsFromBarRef = useRef(false)
  const barPriceRef = useRef<HTMLButtonElement>(null)
  const openPriceDetails = useCallback(() => {
    detailsFromBarRef.current = stickyBarActive && !widgetModalOpen
    if (widgetModalOpen) {
      detailsFromWidgetModalRef.current = true
      setWidgetModalOpen(false)
    }
    setPriceDetailsOpen(true)
  }, [widgetModalOpen, stickyBarActive])

  useEffect(() => {
    if (priceDetailsOpen || !detailsFromBarRef.current) return
    detailsFromBarRef.current = false
    // Same commit remounted the bar, so the ref is populated by now.
    barPriceRef.current?.focus()
  }, [priceDetailsOpen])

  const closePriceDetails = useCallback(() => {
    setPriceDetailsOpen(false)
    if (detailsFromWidgetModalRef.current) {
      detailsFromWidgetModalRef.current = false
      setWidgetModalOpen(true)
    }
  }, [])

  // Floating-bar state machine, mirroring the WP sticky show/hide flow:
  // no dates → starting price + min stay; quote in flight → skeleton;
  // quote ready → per-night · total + info icon; quote failed → error text.
  const barState: StickyBarState = useMemo(() => {
    if (!checkin || !checkout) return 'initial'
    // Keep showing the last quote while a coupon applies; only date/guest changes fall
    // back to the loading skeleton.
    if (quoteSettling && !couponApplying) return 'loading'
    if (quote && currentRatePlan) return 'quoted'
    if (quoteNotice) return 'error'
    return 'loading'
  }, [checkin, checkout, quoteSettling, couponApplying, quote, currentRatePlan, quoteNotice])

  const barDatesText = useMemo(() => {
    if (!checkin || !checkout) return null
    const fmt = (iso: string) => {
      const dt = DateTime.fromISO(iso)
      if (!localeTag) return dt.toFormat('MMM d')
      try {
        return dt.setLocale(localeTag).toFormat('MMM d')
      } catch {
        return dt.toFormat('MMM d')
      }
    }
    return `${fmt(checkin)} - ${fmt(checkout)}`
  }, [checkin, checkout, localeTag])

  const barMinStayText = options.showMinStay && effectiveMinStay > 0
    ? _t('label_min_stay').replace('{{value}}', `${effectiveMinStay} ${_t(effectiveMinStay === 1 ? 'unit_night_one' : 'unit_night_other')}`)
    : null

  // Bar button (WP .modal-button): with dates (or no datepicker) it opens the
  // full-form widget modal; otherwise the calendar modal.
  const handleBarButton = useCallback(() => {
    if ((checkin && checkout) || !options.showDatepicker) setWidgetModalOpen(true)
    else setCalendarOpen(true)
  }, [checkin, checkout, options.showDatepicker])

  // Tapping the date range on the bar reopens the calendar; the skip guard
  // stops the initial sync echo from instantly closing it (same as the inputs).
  const openCalendarFromBar = useCallback(() => {
    if (checkin && checkout) skipCalendarClose.current = true
    setCalendarOpen(true)
  }, [checkin, checkout])

  // Inline breakdown block, rendered above or below the Total row (#1064
  // Wave 3). Null when the feature is off or the model came out empty.
  const inlineBreakdown = priceBreakdownConfig && breakdownRows ? (
    <InlinePriceBreakdown
      config={priceBreakdownConfig}
      rows={breakdownRows}
      formatPrice={formatPrice}
      relabel={relabelPriceLabel}
      idPrefix={`hr-pb-${widgetId}`}
      collapsedTotal={displayedTotal !== null ? formatPrice(displayedTotal) : null}
      open={breakdownOpen}
      // `breakdownOpen` is boolean|undefined (undefined = "guest hasn't touched
      // it, use openByDefault"), so it can't take the view's updater form
      // directly — resolve it against the current value here.
      onOpenChange={(next) => setBreakdownOpen((prev) => (typeof next === 'function' ? next(prev ?? false) : next))}
      expOpen={breakdownExpOpen}
      onExpOpenChange={setBreakdownExpOpen}
      taxOpen={breakdownTaxOpen}
      onTaxOpenChange={setBreakdownTaxOpen}
    />
  ) : null

  // The form body renders in exactly one place: inline on desktop (or when
  // the bar is disabled), or inside the bar's widget modal on mobile — the
  // React equivalent of WP moving the widget DOM into the sticky modal.
  const formBody = (
        <div className="homelocal-booking-widget-body">
          {/* Card header (#823, BK-card): price left, stay pill right. Replaces
              the old rent-unit + total-nights stack. */}
          <div className="bk-header">
            <div className="bk-header-price">
              {/* Base price per night (before dates selected) */}
              {options.showAvgNightPrice && property && !quote && (
                <div className="rent-unit">
                  <span className="rent-after-date rent-amount">{formatPrice(property.base_daily_rate)}</span>
                  <span className="bk-price-unit">{_t('unit_per_night')}</span>
                  {renderBedroomHint(property.base_daily_rate)}
                </div>
              )}
              {/* Avg price per night (after quote) */}
              {options.showAvgNightPrice && nightlyRate && quote && !(quoteSettling && !couponApplying) && (
                <div className="rent-unit">
                  {nightlyRate.original ? <del className="rent-deleted">{formatPrice(nightlyRate.original)}</del> : null}
                  <span className="rent-after-date rent-amount">{formatPrice(nightlyRate.price)}</span>
                  <span className="bk-price-unit">{_t('unit_per_night')}</span>
                  {renderBedroomHint(nightlyRate.price)}
                </div>
              )}
            </div>
            {/* Stay pill (WP: total_nights_text / min_nights_text) */}
            {(nights > 0 || (options.showMinStay && effectiveMinStay > 0)) && (
              <span className="bk-pill">
                {nights > 0
                  ? `${nights} ${_t(nights === 1 ? 'unit_night_one' : 'unit_night_other')}`
                  : _t(effectiveMinStay === 1 ? 'pill_min_stay_one' : 'pill_min_stay').replace('{{n}}', String(effectiveMinStay))}
              </span>
            )}
          </div>

          {/* Joined field group (#823 round 2): date cells on top, collapsed
              guests field below, one shared border. The guests panel expands
              over the content underneath, Airbnb-style. */}
          <div className="bk-field-wrap">
            <div className="bk-field-group">
              {options.showDatepicker && (
                <div className="booking-date-inputs bk-dates">
                  <div className="booking-date-field">
                    <label className="hk-form-label bk-lbl" htmlFor="checkin_placeholder">{_t('label_check_in')}</label>
                    <input
                      type="text"
                      id="checkin_placeholder"
                      className="booking-input"
                      placeholder={_t('placeholder_add_date')}
                      value={checkin ? formatDate(checkin) : ''}
                      readOnly
                      onClick={() => {
                        if (checkin && checkout) skipCalendarClose.current = true
                        setCalendarOpen(true)
                      }}
                    />
                  </div>
                  <div className="booking-date-field">
                    <label className="hk-form-label bk-lbl" htmlFor="checkout_placeholder">{_t('label_check_out')}</label>
                    <input
                      type="text"
                      id="checkout_placeholder"
                      className="booking-input"
                      placeholder={_t('placeholder_add_date')}
                      value={checkout ? formatDate(checkout) : ''}
                      readOnly
                      onClick={() => {
                        if (checkin && checkout) skipCalendarClose.current = true
                        setCalendarOpen(true)
                      }}
                    />
                  </div>
                </div>
              )}
              <button
                type="button"
                ref={guestTriggerRef}
                className="guests-row bk-guests-field"
                aria-expanded={guestPickerOpen}
                aria-controls={guestPanelId}
                onClick={() => setGuestPickerOpen((o) => !o)}
                onKeyDown={(e) => {
                  // The panel's handler only sees keys from INSIDE it — a
                  // keyboard user who opened the picker still has focus here.
                  if (e.key === 'Escape' && guestPickerOpen) {
                    e.stopPropagation()
                    closeGuestPicker()
                  }
                }}
              >
                <span className="bk-guests-field-text">
                  <span className="bk-lbl">{_t('label_guests')}</span>
                  <span className="bk-guests-summary">{guestSummary}</span>
                </span>
                <svg
                  className={`bk-chevron${guestPickerOpen ? ' bk-chevron-open' : ''}`}
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>

            {guestPickerOpen && (
              <div
                className="bk-guest-panel"
                role="group"
                aria-label={_t('aria_guest_selection')}
                id={guestPanelId}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    // Keep the Escape from also dismissing an enclosing modal
                    // (the sticky-bar widget modal): one press, one layer.
                    e.stopPropagation()
                    closeGuestPicker()
                  }
                }}
              >
                {!guestBreakdown ? (
                  <div className="bk-guest-line">
                    <span className="bk-guest-label" id="bk-guests-label">{_t('label_guests')}</span>
                    <GuestStepper
                      value={guests}
                      min={1}
                      max={maxGuests}
                      decLabel={_t('aria_decrease_guests')}
                      incLabel={_t('aria_increase_guests')}
                      labelledBy="bk-guests-label"
                      onChange={(v) => { userPickedGuestsRef.current = true; setGuests(v) }}
                    />
                  </div>
                ) : (
                  <>
                    <div className="bk-guest-line">
                      <span className="bk-guest-who">
                        <span className="bk-guest-label" id="bk-adults-label">{_t('label_adults')}</span>
                        <span className="bk-guest-hint">{_t('hint_adults_age')}</span>
                      </span>
                      <GuestStepper
                        value={guests}
                        min={1}
                        max={Math.max(1, maxGuests - childrenCount)}
                        decLabel={_t('aria_decrease_adults')}
                        incLabel={_t('aria_increase_adults')}
                        labelledBy="bk-adults-label"
                        onChange={(v) => { userPickedGuestsRef.current = true; setGuests(v) }}
                      />
                    </div>
                    <div className="bk-guest-line">
                      <span className="bk-guest-who">
                        <span className="bk-guest-label" id="bk-children-label">{_t('label_children')}</span>
                        <span className="bk-guest-hint">{_t('hint_children_age')}</span>
                      </span>
                      <GuestStepper
                        value={childrenCount}
                        min={0}
                        max={Math.max(0, maxGuests - guests)}
                        decLabel={_t('aria_decrease_children')}
                        incLabel={_t('aria_increase_children')}
                        labelledBy="bk-children-label"
                        onChange={(v) => { userPickedGuestsRef.current = true; setChildrenCount(v) }}
                      />
                    </div>
                    {showInfants && (
                      <div className="bk-guest-line">
                        <span className="bk-guest-who">
                          <span className="bk-guest-label" id="bk-infants-label">{_t('label_infants')}</span>
                          <span className="bk-guest-hint">{_t('hint_infants_age')}</span>
                        </span>
                        <GuestStepper
                          value={infantsCount}
                          min={0}
                          max={INFANTS_MAX}
                          decLabel={_t('aria_decrease_infants')}
                          incLabel={_t('aria_increase_infants')}
                          labelledBy="bk-infants-label"
                          onChange={setInfantsCount}
                        />
                      </div>
                    )}
                    {showPets && (
                      <>
                        <div className="bk-guest-line">
                          <span className="bk-guest-who">
                            <span className="bk-guest-label" id="bk-pets-label">{_t('label_pets')}</span>
                            {petsLocked && (
                              <span className="bk-guest-hint">{_t('hint_pets_not_allowed')}</span>
                            )}
                          </span>
                          <GuestStepper
                            value={petsCount}
                            min={0}
                            max={occupancy.maxPets}
                            disabled={petsLocked}
                            decLabel={_t('aria_decrease_pets')}
                            incLabel={_t('aria_increase_pets')}
                            labelledBy="bk-pets-label"
                            onChange={setPetsCount}
                            onAttemptOverMax={hasConfiguredPetsCap ? showPetsCapNotice : undefined}
                          />
                        </div>
                        {/* At-cap note (#1230 follow-up, #1407; toolkit guest-picker.js:242-254
                            showPetsTooltip): fires on an attempted over-cap increment AND
                            on a URL-seeded value getting reconciled down — no silent
                            clamping. Toolkit-parity: only against a REAL configured
                            ceiling (property or feed-level), never the platform's own
                            generic PETS_MAX fallback. */}
                        {petsCapNoticeVisible && (
                          <p className="bk-guest-pets-cap-note" role="status">{petsCapNoticeText}</p>
                        )}
                        {/* Pet notice (#1230): toolkit shows it at ANY pets > 0, hides at 0. */}
                        {options.showPetNotice && options.petNoticeText && petsCount > 0 && (
                          <p className="bk-guest-pet-notice" role="note">{options.petNoticeText}</p>
                        )}
                      </>
                    )}
                  </>
                )}
                <p className="bk-guest-max-note">
                  {_t(showInfants ? 'guest_max_note_with_infants' : 'guest_max_note')
                    .replace('{{n}}', String(maxGuests))
                    .replace('{{unit}}', _t(maxGuests === 1 ? 'unit_guest_one' : 'unit_guest_other'))}
                </p>
                <div className="bk-guest-panel-actions">
                  <button type="button" className="bk-guest-close" onClick={closeGuestPicker}>
                    {_t('button_close')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Static selected dates (datepicker hidden) */}
          {!options.showDatepicker && options.showSelectedDates && (checkin || checkout) && (
            <div className="hk-flex booking-date-inputs booking-static-dates">
              <div className="booking-date-field">
                <span className="hk-form-label">{_t('label_check_in_static')}</span>
                <span className="booking-static-date">{checkin ? formatDate(checkin) : '—'}</span>
              </div>
              <div className="booking-date-field">
                <span className="hk-form-label">{_t('label_check_out_static')}</span>
                <span className="booking-static-date">{checkout ? formatDate(checkout) : '—'}</span>
              </div>
            </div>
          )}

          {/* Price loader — first load + date/guest changes; a coupon apply keeps the
              overview visible and spins the Apply button instead (#627). */}
          {quoteSettling && !couponApplying && (
            <div className="price-loader">
              <div className="booking-spinner" />
            </div>
          )}

          {/* Quote notice */}
          {quoteNotice && (
            <div className="quote-notice">
              <div className={`hk-alert hk-alert-${quoteNotice.type}`}>
                {quoteNotice.text}
              </div>
            </div>
          )}

          {/* Booking overview - shown when quote is ready. Stays mounted through a
              coupon apply (keepPreviousData holds the prior quote) so the coupon input
              and layout don't collapse (#627); only date/guest changes hide it. */}
          {quote && currentRatePlan && !(quoteSettling && !couponApplying) && (
            <div className="homelocal-booking-overview">
              {/* Rate plans (if multiple) */}
              {visibleRatePlans.length > 1 && (
                <div className="rateplans">
                  {visibleRatePlans.map((rp) => (
                    <label key={rp.id} className="hk-flex hk-flex-middle hk-margin-small rateplan">
                      <span className="hk-flex-none">
                        <input
                          type="radio"
                          name="rateplan"
                          className="hk-radio"
                          value={rp.id}
                          checked={ratePlanSelected === rp.id}
                          onChange={() => setRatePlanSelected(rp.id)}
                        />
                      </span>
                      <span className="hk-flex-auto hk-margin-small-left">
                        <div>
                          <span className="hk-text-bold">{rp.title}</span>
                          {rp.tooltip && (
                            <span
                              className="rateplan-info hk-margin-small-left"
                              role="img"
                              tabIndex={0}
                              title={rp.tooltip}
                              aria-label={rp.tooltip}
                            >
                              ⓘ
                            </span>
                          )}
                        </div>
                        {rp.description && (
                          <div className="hk-text-small">{rp.description}</div>
                        )}
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {hiddenPlanNote && (
                <div className="rateplan-coupon-note hk-text-small" role="note">{hiddenPlanNote}</div>
              )}

              {/* Coupon section */}
              {options.showCoupon && (
              <div className="coupon-row">
                <label className="hk-flex hk-flex-middle">
                  <input
                    type="checkbox"
                    id="have_coupon"
                    checked={showCouponInput}
                    onChange={(e) => setShowCouponInput(e.target.checked)}
                  />
                  <span className="hk-margin-small-left hk-text-small">{_t('label_have_coupon')}</span>
                </label>
                {showCouponInput && (
                  <div className="coupon-input-row">
                    <input
                      type="text"
                      id="coupon"
                      className="booking-input"
                      placeholder={_t('placeholder_coupon_code')}
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleApplyCoupon() }}
                    />
                    <button
                      id="apply-coupon-btn"
                      className="coupon-apply-btn"
                      onClick={handleApplyCoupon}
                      disabled={couponApplying}
                    >
                      {couponApplying ? (
                        <span className="coupon-apply-loading">
                          <span className="coupon-apply-spinner" />
                          {_t('button_coupon_applying')}
                        </span>
                      ) : (
                        _t('button_coupon_apply')
                      )}
                    </button>
                  </div>
                )}
                {couponNotice && (
                  <div className="coupon-notice">
                    <div className={`hk-alert hk-alert-${couponNotice.type}`}>{couponNotice.text}</div>
                  </div>
                )}
              </div>
              )}

              {/* Inline price breakdown — above the Total row (WP default) */}
              {priceBreakdownConfig?.position === 'above' && inlineBreakdown}

              {/* Total. #1062 item 1 (Eli decision 2026-09-04, option A): the
                  pre-tax figure (totalAfterTax off, the #685 WP-parity
                  default for fresh widgets) was labelled bare "Total" with
                  nothing telling the guest tax is excluded. Label only, no
                  behaviour change — the after-tax case keeps its existing
                  "Total after tax" text untouched. */}
              <div className="hk-margin-small">
                <div className="hk-flex hk-flex-between total">
                  <span className="total-price-label hk-text-bold">{options.totalAfterTax ? _t('label_total_after_tax') : _t('label_total_before_taxes')}</span>
                  <span className="total-price hk-text-bold">
                    {originalTotal !== null && <><del className="total-price-original">{formatPrice(originalTotal)}</del>{' '}</>}
                    {displayedTotal !== null && formatPrice(displayedTotal)}
                  </span>
                </div>
                {!options.totalAfterTax && (
                  <div className="bk-guest-hint total-price-tax-note">{_t('note_taxes_at_checkout')}</div>
                )}
              </div>

              {/* Inline price breakdown — below the Total row */}
              {priceBreakdownConfig?.position === 'below' && inlineBreakdown}

              {/* Show price details button. hideDetailsButton only bites while
                  the inline breakdown actually renders (WP render() bails on
                  zero rows BEFORE it hides the modal button). */}
              {options.showPriceDetails && !(priceBreakdownConfig?.hideDetailsButton && inlineBreakdown) && (
                <div className="price-details-button-container">
                  <button
                    className="hk-button-text price-details-modal-button"
                    type="button"
                    onClick={openPriceDetails}
                  >
                    {options.detailsButtonText || 'Show Price Details'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Checkout button */}
          {options.showCheckoutButton && (
            <button
              className="checkout-button"
              disabled={isButtonDisabled}
              onClick={handleCheckout}
            >
              {buttonText}
            </button>
          )}

          {/* Discount offer — centred note under the CTA (#823, BK-card) */}
          {discountText && <p className="bk-note">{discountText}</p>}
        </div>
  )

  return (
    <>
      {/* The card chrome (border/shadow) applies to the inline render only —
          inside the sticky-bar modal the dialog already frames the form. */}
      {!stickyBarActive && (
        <div className="homelocal-booking-widget bk-card" ref={widgetBodyRef}>
          {formBody}
        </div>
      )}

      {/* Calendar Modal - prerendered, toggled via visibility */}
      {calendarConfig && (
        <CalendarModal
          calendarConfig={calendarConfig}
          calendarWidgetId={calendarWidgetId}
          feedId={feedId}

          resolvedTheme={resolvedTheme}
          initialCheckin={checkin}
          initialCheckout={checkout}
          visible={calendarOpen}
          onClose={() => setCalendarOpen(false)}
          _t={_t}
        />
      )}

      {/* Price Details Modal */}
      {priceDetailsOpen && currentRatePlan && (
        <PriceDetailsModal
          ratePlan={currentRatePlan}
          formatPrice={formatPrice}
          showSubtotal={options.showSubtotal !== false}
          labelReplacements={options.priceLabelReplacements}
          priceBreakdown={priceBreakdownConfig}
          onClose={closePriceDetails}
          _t={_t}
        />
      )}

      {/* Widget modal (#604): the full booking form opened from the floating
          bar — WP moves the in-page widget into its sticky modal; here the
          form body just renders inside this dialog instead of inline. */}
      {stickyBarActive && (
        <Dialog open={widgetModalOpen} onOpenChange={(open) => { if (!open) setWidgetModalOpen(false) }}>
          <DialogContent
            className={`booking-modal-content booking-widget-modal${resolvedTheme === 'dark' ? ' dark' : ''}`}
            elevateDom
            cssString={cssString}
            title={_t('dialog_title')}
            onEscapeKeyDown={(e) => {
              // Radix's Escape handler runs on document CAPTURE, before the guest
              // panel's own bubble-phase handler ever sees the key — without this,
              // one Escape dismisses the picker AND the whole booking modal. One
              // press, one layer: swallow the dialog-close and shut only the picker.
              if (guestPickerOpen) {
                e.preventDefault()
                closeGuestPicker()
              }
            }}
          >
            <div className={`homerunner-booking-container homerunner-booking-app${resolvedTheme === 'dark' ? ' dark' : ''}`}>
              <div className="homelocal-booking-widget">
                {formBody}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Floating booking bar (#604, WP sticky-widget parity). Hidden while
          the widget or price-details modal is open, like the WP plugin — and
          while the calendar modal is open (#919): the bar's z-index sits above
          the elevated dialog host, and Radix zeroes body pointer-events under
          a modal, so a bar left mounted covered the full-screen calendar's
          footer while taps on it fell through to Clear Dates / Close. WP does
          not keep its bar over the calendar either: its calendar modal is
          z 9999990 against the bar's 999999. Gated on the modal actually
          RENDERING (calendarConfig resolved), not on the open flag alone —
          with no calendar config the flag flips but nothing opens, and a bar
          that vanished then would leave the visitor with no control at all. */}
      {stickyBarActive && !widgetModalOpen && !priceDetailsOpen && !(calendarOpen && calendarConfig) && (
        <StickyBar
          state={barState}
          startingPrice={options.showAvgNightPrice && property ? formatPrice(property.base_daily_rate) : null}
          minStayText={barMinStayText}
          nightPrice={nightlyRate ? formatPrice(nightlyRate.price) : null}
          nightPriceOriginal={nightlyRate?.original ? formatPrice(nightlyRate.original) : null}
          totalPrice={displayedTotal !== null ? formatPrice(displayedTotal) : null}
          errorText={quoteNotice?.text || null}
          datesText={barDatesText}
          showPriceDetails={options.showPriceDetails !== false}
          buttonText={buttonText}
          buttonDisabled={quoteSettling || checkoutLoading}
          className={options.sticky?.className}
          buttonClassName={options.sticky?.buttonClassName}
          onButtonClick={handleBarButton}
          onDatesClick={openCalendarFromBar}
          onPriceClick={openPriceDetails}
          priceRef={barPriceRef}
          _t={_t}
        />
      )}
    </>
  )
}
